"use client"

import { IFsNodeSchema } from '@cloudike/web_files'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { RootState } from 'shared/config/store'

import { userApi } from '../../../../shared/api/userApi'
import { AppState, AuthModalType } from '../types/app'
import { getCurrentAppId } from '../../../../shared/utils'

export enum AUTH_MODAL_MODE {
  SIGNIN = 'signin',
  SIGNUP = 'signup',
  EMAIL_VERIFICATION = 'email_verification',
}

export enum SORT_KEY {
  UPLOAD_DATE = 'created',
  LAST_UPDATED = 'updated',
  NAME = 'name',
  FILE_TYPE = 'type',
  EXT = 'ext',
}

export enum SORT_ORDER {
  ASC = 'asc',
  DESC = 'desc',
}

export const SORTING_OPTIONS = [
  {
    key: SORT_KEY.UPLOAD_DATE,
    label: 'Upload Date',
  },
  {
    key: SORT_KEY.LAST_UPDATED,
    label: 'Last Updated',
  },
  {
    key: SORT_KEY.NAME,
    label: 'Name',
  },
  {
    key: SORT_KEY.EXT,
    label: 'File type',
  }
]



// localStorage keys
const SORTING_STORAGE_KEY = 'dam-sorting-settings'

// Helper functions for localStorage
const getSortingFromStorage = () => {
  try {
    const stored = localStorage.getItem(SORTING_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        selectedSort: parsed.selectedSort || SORTING_OPTIONS[0].key,
        sortOrder: parsed.sortOrder || SORT_ORDER.DESC
      }
    }
  } catch (error) {
    console.warn('Failed to parse sorting settings from localStorage:', error)
  }
  return {
    selectedSort: SORTING_OPTIONS[0].key,
    sortOrder: SORT_ORDER.DESC
  }
}

const saveSortingToStorage = (selectedSort: string, sortOrder: SORT_ORDER) => {
  try {
    localStorage.setItem(SORTING_STORAGE_KEY, JSON.stringify({
      selectedSort,
      sortOrder
    }))
  } catch (error) {
    console.warn('Failed to save sorting settings to localStorage:', error)
  }
}

const resetSortingToDefault = () => {
  try {
    localStorage.removeItem(SORTING_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to reset sorting settings in localStorage:', error)
  }
}

const initialState: AppState = {
  isAuthModalOpen: false,
  authModalType: 'default',
  authModalInPublicLink: false,
  isCreateTeamModalOpen: false,
  isJoinTeamModalOpen: false,
  isForgotPassModalOpen: false,
  isInviteMembersModalOpen: false,
  isAddNewBoardModalOpen: false,
  isSelectTargetBoardToUploadModalOpen: false,
  isShowAgreementModalOpen: false,
  isPlansModalOpen: false,
  modalType: null,
  modalBoardData: null,
  renamingBoardId: null,
  contentTypesUpload: '',
  contentTypesDropzone: {},
  extensionAvailableForUploading: [],
  webSocketState: null,
  isConfirmationSendToModalOpen: false,
  isConfirmationErrorModalOpen: false,
  isConfirmationSuccessModalOpen: false,
  sendToEmail: '',
  expireDate: '',
  authModalMode: AUTH_MODAL_MODE.SIGNIN,
  emailVerification: {
    email: '',
    code: '',
    timeLeft: 60,
    canResend: true,
    isLoading: false,
    error: null,
  },
  sortingOptions: SORTING_OPTIONS,
  sortOrder: getSortingFromStorage().sortOrder,
  defaultSortingOption: SORTING_OPTIONS[0],
  selectedSort: getSortingFromStorage().selectedSort,
  appId: getCurrentAppId(),
}

export const addContentTypesThunk = createAsyncThunk(
  'app/addContentTypesThunk',
  async function (_, { dispatch, getState }) {
    const state = (getState() as RootState)
    const id = state.user.user.id

    const response = await userApi.getContentTypes(id)

    const initialContentTypesForDropzone = {
      'image/*': [],
      'video/*': [],
    }

    const contentTypes = response.content_types
    const extensions = response.extensions

    const addPointForExtension = extensions.map(item => '.' + item)

    for (const key in initialContentTypesForDropzone) {
      initialContentTypesForDropzone[key] = [...addPointForExtension]
    }

    const contentTypesForUpload = contentTypes.join() + ',' + addPointForExtension.join()

    const contentTypesForDropzone = contentTypes.reduce((object, key) => ({ ...object, [key]: [] }), {})

    if (!!contentTypes && !!extensions) {
      dispatch(actions.setContentTypesUpload(contentTypesForUpload))
      dispatch(actions.setContentTypesDropzone({ ...contentTypesForDropzone }))
      dispatch(actions.setExtensionsAvailableForUploading(addPointForExtension))
    }
  }
)

export const appSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setModalBoardData: (state, action: PayloadAction<{ boardIds?: string [] | null; boards?: IFsNodeSchema[] | null, parentId?: string | null } | null>) => {
      state.modalBoardData = action.payload
    },
    setModalType: (state, action: PayloadAction<BoardToolbarAction | null>) => {
      state.modalType = action.payload
    },
    setIsAuthModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAuthModalOpen = action.payload
    },
    setAuthModalState: (state, action: PayloadAction<{ isOpen: boolean; type?: AuthModalType, mode?: AUTH_MODAL_MODE, inPublicLink?: boolean }>) => {
      state.isAuthModalOpen = action.payload.isOpen

      if (action.payload.type) {
        state.authModalType = action.payload.type
      }

      if (action.payload.mode) {
        state.authModalMode = action.payload.mode
      }

      if (action.payload.inPublicLink) {
        state.authModalInPublicLink = action.payload.inPublicLink
      }
    },
    setIsCreateTeamModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreateTeamModalOpen = action.payload
    },
    setIsJoinTeamModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isJoinTeamModalOpen = action.payload
    },
    setIsAddNewBoardModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAddNewBoardModalOpen = action.payload
    },
    setIsSelectTargetBoardToUploadModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isSelectTargetBoardToUploadModalOpen = action.payload
    },
    setIsForgotPassModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isForgotPassModalOpen = action.payload
    },
    setIsInviteMembersModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isInviteMembersModalOpen = action.payload
    },
    setIsPlansModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isPlansModalOpen = action.payload
    },
    setContentTypesUpload: (state, action) => {
      state.contentTypesUpload = action.payload
    },
    setContentTypesDropzone: (state, action) => {
      state.contentTypesDropzone = action.payload
    },
    setExtensionsAvailableForUploading: (state, action) => {
      state.extensionAvailableForUploading = action.payload
    },
    setWebSocketState: (state, action) => {
      state.webSocketState = action.payload
    },
    setIsConfirmationSendToModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmationSendToModalOpen = action.payload
    },
    setSendToEmail: (state, action: PayloadAction<string>) => {
      state.sendToEmail = action.payload
    },
    setExpireDate: (state, action: PayloadAction<string>) => {
      state.expireDate = action.payload
    },
    setAuthModalMode: (state, action: PayloadAction<AUTH_MODAL_MODE>) => {
      state.authModalMode = action.payload
    },
    setIsConfirmationErrorModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmationErrorModalOpen = action.payload
    },
    setIsConfirmationSuccessModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmationSuccessModalOpen = action.payload
    },
    setEmailVerificationEmail: (state, action: PayloadAction<string>) => {
      state.emailVerification.email = action.payload
    },
    setEmailVerificationCode: (state, action: PayloadAction<string>) => {
      state.emailVerification.code = action.payload
    },
    setEmailVerificationTimeLeft: (state, action: PayloadAction<number>) => {
      state.emailVerification.timeLeft = action.payload
    },
    setEmailVerificationCanResend: (state, action: PayloadAction<boolean>) => {
      state.emailVerification.canResend = action.payload
    },
    setEmailVerificationLoading: (state, action: PayloadAction<boolean>) => {
      state.emailVerification.isLoading = action.payload
    },
    setEmailVerificationError: (state, action: PayloadAction<string | null>) => {
      state.emailVerification.error = action.payload
    },
    resetEmailVerification: (state) => {
      state.emailVerification = {
        email: '',
        code: '',
        timeLeft: 60,
        canResend: true,
        isLoading: false,
        error: null,
      }
    },
    setSelectedSort: (state, action: PayloadAction<string>) => {
      state.selectedSort = action.payload
      saveSortingToStorage(action.payload, state.sortOrder)
    },
    setSortOrder: (state, action: PayloadAction<SORT_ORDER>) => {
      state.sortOrder = action.payload
      saveSortingToStorage(state.selectedSort, action.payload)
    },
    resetSortingSettings: (state) => {
      state.selectedSort = SORTING_OPTIONS[0].key
      state.sortOrder = SORT_ORDER.DESC
      resetSortingToDefault()
    }
  },
})

export const {
  setIsAuthModalOpen,
  setAuthModalState,
  setIsCreateTeamModalOpen,
  setIsJoinTeamModalOpen,
  setIsAddNewBoardModalOpen,
  setIsForgotPassModalOpen,
  setIsInviteMembersModalOpen,
  setIsSelectTargetBoardToUploadModalOpen,
  setModalType,
  setWebSocketState,
  setIsConfirmationSendToModalOpen,
  setSendToEmail,
  setExpireDate,
  setAuthModalMode,
  setIsConfirmationErrorModalOpen,
  setIsConfirmationSuccessModalOpen,
  setEmailVerificationEmail,
  setEmailVerificationCode,
  setEmailVerificationTimeLeft,
  setEmailVerificationCanResend,
  setEmailVerificationLoading,
  setEmailVerificationError,
  resetEmailVerification,
  resetSortingSettings,
  setIsPlansModalOpen,
} = appSlice.actions

export const actions = appSlice.actions
export const appReducer = appSlice.reducer

// Export localStorage functions for external use
export { getSortingFromStorage, saveSortingToStorage, resetSortingToDefault }
